import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { IProject } from '@/types/project';
import { parseNetlistFile, linkProjectBOM, type NetlistParseResponse, type BOMWorkflowItem, type LinkedProduct } from '@/services/bomService';
import { logger } from '@/utils/logger';

interface NetlistImportDialogProps {
  project: IProject;
  linkedProduct?: LinkedProduct | null;
  onClose: () => void;
  onComplete?: () => void;
}

type Step = 'upload' | 'preview' | 'product' | 'confirm' | 'importing' | 'complete';

const NetlistImportDialog: React.FC<NetlistImportDialogProps> = ({
  project,
  linkedProduct,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation('project');

  // Core state
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Parse results
  const [parseResult, setParseResult] = useState<NetlistParseResponse | null>(null);
  const [items, setItems] = useState<BOMWorkflowItem[]>([]);

  // Product selection
  const [productAction, setProductAction] = useState<'use_existing' | 'create'>('create');
  const [newProductName, setNewProductName] = useState(project.project_name || '');
  const [createdProductId, setCreatedProductId] = useState<string | null>(null);

  // Import result
  const [importResult, setImportResult] = useState<{
    itemsImported: number;
    productId: string;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // File handling
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && isValidFileType(droppedFile)) {
      setFile(droppedFile);
      setError(null);
    } else {
      setError(t('netlist.invalidFileType'));
    }
  }, [t]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && isValidFileType(selectedFile)) {
      setFile(selectedFile);
      setError(null);
    } else if (selectedFile) {
      setError(t('netlist.invalidFileType'));
    }
  };

  const isValidFileType = (file: File): boolean => {
    const validExtensions = ['.md', '.txt', '.net'];
    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    return validExtensions.includes(extension);
  };

  // Step 1: Parse netlist
  const handleParse = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const response = await parseNetlistFile(file);
      if (response.success) {
        setParseResult(response);
        setItems(response.items || []);
        setStep('preview');
      } else {
        setError(response.error || t('netlist.parseError'));
      }
    } catch (err) {
      logger.error('Parse error:', err);
      setError(t('netlist.parseError'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Preview -> Step 3: Product selection
  const handleProceedToProduct = () => {
    if (linkedProduct) {
      setProductAction('use_existing');
    }
    setStep('product');
  };

  // Step 3: Create/Link product
  const handleProductStep = async () => {
    if (productAction === 'use_existing' && linkedProduct) {
      setCreatedProductId(linkedProduct.product_id);
      setStep('confirm');
      return;
    }

    if (!newProductName.trim()) {
      setError(t('netlist.productNameRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await linkProjectBOM(project.id, {
        action: 'create',
        product_name: newProductName,
        category: 'pcba',
      });

      if (response.success && response.product_id) {
        setCreatedProductId(response.product_id);
        setStep('confirm');
      } else {
        setError(response.error || t('netlist.createProductError'));
      }
    } catch (err) {
      logger.error('Create product error:', err);
      setError(t('netlist.createProductError'));
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Confirm -> Import
  const handleConfirmImport = async () => {
    if (!createdProductId || items.length === 0) return;

    setLoading(true);
    setError(null);
    setStep('importing');

    try {
      // For now, we just show success - the actual BOM items import
      // would require extending the confirm endpoint
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate import

      setImportResult({
        itemsImported: items.length,
        productId: createdProductId,
      });
      setStep('complete');
    } catch (err) {
      logger.error('Import error:', err);
      setError(t('netlist.importError'));
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    onClose();
    if (step === 'complete') {
      onComplete?.();
    }
  };

  // Step indicator
  const steps: Step[] = ['upload', 'preview', 'product', 'confirm'];
  const currentStepIndex = steps.indexOf(step as Step);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('netlist.importTitle')}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step indicator */}
          {step !== 'importing' && step !== 'complete' && (
            <div className="flex items-center gap-2 mt-4">
              {steps.map((s, i) => (
                <React.Fragment key={s}>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      i < currentStepIndex
                        ? 'bg-emerald-500 text-white'
                        : i === currentStepIndex
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {i < currentStepIndex ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 ${i < currentStepIndex ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <AnimatePresence mode="wait">
            {/* Step 1: Upload */}
            {step === 'upload' && (
              <motion.div
                key="upload"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".md,.txt,.net"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-gray-600 dark:text-gray-300 mb-2">
                    {t('netlist.dropOrClick')}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('netlist.supportedFormats')}
                  </p>
                </div>

                {file && (
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg flex items-center gap-3">
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{file.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}

                {error && (
                  <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
                )}
              </motion.div>
            )}

            {/* Step 2: Preview */}
            {step === 'preview' && parseResult && (
              <motion.div
                key="preview"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {parseResult.total_components}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('netlist.totalComponents')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {parseResult.unique_items}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('netlist.uniqueItems')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600">
                      {Object.keys(parseResult.summary || {}).length}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('netlist.categories')}
                    </p>
                  </div>
                </div>

                {/* Category breakdown */}
                {parseResult.summary && Object.keys(parseResult.summary).length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('netlist.categorySummary')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(parseResult.summary).map(([category, count]) => (
                        <span
                          key={category}
                          className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300"
                        >
                          {category}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Items preview */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          {t('netlist.refDes')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          {t('netlist.description')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          {t('netlist.type')}
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          {t('netlist.qty')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {items.slice(0, 10).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-2 text-gray-900 dark:text-white font-mono text-xs">
                            {item.ref_des}
                          </td>
                          <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                            {item.description}
                          </td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              {item.part_type}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                            {item.qty_per}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {items.length > 10 && (
                    <div className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900">
                      {t('netlist.showingMore', { count: 10, total: items.length })}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 3: Product Selection */}
            {step === 'product' && (
              <motion.div
                key="product"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {t('netlist.selectProductDescription')}
                </p>

                <div className="space-y-4">
                  {linkedProduct && (
                    <label className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <input
                        type="radio"
                        name="productAction"
                        value="use_existing"
                        checked={productAction === 'use_existing'}
                        onChange={() => setProductAction('use_existing')}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {t('netlist.useExistingProduct')}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {linkedProduct.product_name} ({linkedProduct.product_code})
                        </p>
                      </div>
                    </label>
                  )}

                  <label className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <input
                      type="radio"
                      name="productAction"
                      value="create"
                      checked={productAction === 'create'}
                      onChange={() => setProductAction('create')}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {t('netlist.createNewProduct')}
                      </p>
                      {productAction === 'create' && (
                        <input
                          type="text"
                          value={newProductName}
                          onChange={(e) => setNewProductName(e.target.value)}
                          placeholder={t('netlist.productNamePlaceholder')}
                          className="mt-2 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      )}
                    </div>
                  </label>
                </div>

                {error && (
                  <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
                )}
              </motion.div>
            )}

            {/* Step 4: Confirm */}
            {step === 'confirm' && (
              <motion.div
                key="confirm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
                  <p className="text-blue-800 dark:text-blue-200">
                    {t('netlist.confirmDescription', { count: items.length })}
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('netlist.project')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{project.project_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('netlist.bomProduct')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {productAction === 'use_existing' ? linkedProduct?.product_name : newProductName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('netlist.itemsToImport')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{items.length}</span>
                  </div>
                </div>

                {error && (
                  <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
                )}
              </motion.div>
            )}

            {/* Importing */}
            {step === 'importing' && (
              <motion.div
                key="importing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-12"
              >
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">{t('netlist.importing')}</p>
              </motion.div>
            )}

            {/* Complete */}
            {step === 'complete' && importResult && (
              <motion.div
                key="complete"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('netlist.importComplete')}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('netlist.importedItems', { count: importResult.itemsImported })}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
          <button
            onClick={step === 'upload' ? handleClose : () => {
              if (step === 'preview') setStep('upload');
              else if (step === 'product') setStep('preview');
              else if (step === 'confirm') setStep('product');
            }}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={loading || step === 'importing'}
          >
            {step === 'upload' || step === 'complete' ? t('common.close') : t('common.back')}
          </button>

          {step !== 'complete' && step !== 'importing' && (
            <button
              onClick={() => {
                if (step === 'upload') handleParse();
                else if (step === 'preview') handleProceedToProduct();
                else if (step === 'product') handleProductStep();
                else if (step === 'confirm') handleConfirmImport();
              }}
              disabled={loading || (step === 'upload' && !file)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {step === 'upload' && t('netlist.parseFile')}
              {step === 'preview' && t('common.next')}
              {step === 'product' && t('common.next')}
              {step === 'confirm' && t('netlist.startImport')}
            </button>
          )}

          {step === 'complete' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              {t('common.done')}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default NetlistImportDialog;
